import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import Breadcrumbs from "@/components/Breadcrumbs";
import { fadeUp, stagger, heroImageMotion } from "@/utils/motionVariants";

/**
 * Reusable product-page hero. Mirrors the CardMachinesPage hero layout so the
 * cluster feels like one system: breadcrumb, eyebrow, H1, subtitle, dual CTA,
 * floating product image on the right (hidden on mobile so the CTA stays
 * above the fold).
 */
export default function ProductHero({ product }) {
  return (
    <section className="hero-gradient py-16 md:py-24" data-testid={`${product.slug}-hero`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <Breadcrumbs className="mb-5" />
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">
              {product.deviceType}
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-4">
              {product.name}
            </motion.h1>
            {product.badges?.length > 0 && (
              <motion.div variants={fadeUp} className="flex flex-wrap gap-2 mb-5">
                {product.badges.map((b) => (
                  <span key={b} className="text-xs font-semibold text-brand-aqua bg-brand-aqua-light border border-brand-aqua/20 rounded-full px-3 py-1">
                    {b}
                  </span>
                ))}
              </motion.div>
            )}
            <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-8">
              {product.heroSubtitle}
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
              <a
                href="#enquire"
                className="bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors"
                data-testid={`${product.slug}-cta-enquire`}
              >
                Get a Quote <ArrowRight size={16} />
              </a>
              <Link
                to="/switch-and-save"
                className="bg-brand-pink-light text-brand-pink hover:bg-brand-pink/15 border border-brand-pink/30 font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors"
                data-testid={`${product.slug}-cta-switch`}
              >
                Switch & Save
              </Link>
            </motion.div>
            <motion.p variants={fadeUp} className="mt-6 flex items-center gap-2 text-sm text-brand-grey">
              <Star size={14} className="text-brand-aqua fill-brand-aqua" aria-hidden="true" />
              Rated 4.8/5 by UK businesses — setup handled by our Cheshire-based team
            </motion.p>
          </motion.div>
          <motion.div {...heroImageMotion} className="hidden lg:flex items-center justify-center">
            {product.heroFramed ? (
              // Solid-background studio renders sit in a white framed card so
              // they read as a deliberate product shot on the hero gradient.
              <div className="bg-white border border-brand-light-grey/60 rounded-3xl shadow-xl overflow-hidden aspect-square w-full max-w-md float-animation">
                <img src={product.heroImage} alt={product.heroImageAlt} className="w-full h-full object-cover" />
              </div>
            ) : (
              <img
                src={product.heroImage}
                alt={product.heroImageAlt}
                className="max-h-96 w-auto object-contain drop-shadow-xl float-animation"
              />
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
